import type { DateRangeFilter, Filters } from '@/contexts/table-context';

export const CAMPAIGNS_ENUM_FILTER_FIELDS = {
  channelIds: 'channel',
  statuses: 'status',
  positionIds: 'position',
  locationIds: 'location',
  brandIds: 'brand',
} as const;

export type CampaignsEnumFilterKey = keyof typeof CAMPAIGNS_ENUM_FILTER_FIELDS;

export type CampaignsFilterField =
  | (typeof CAMPAIGNS_ENUM_FILTER_FIELDS)[CampaignsEnumFilterKey]
  | 'date_range';

export type CampaignsFilterOption = {
  label: string;
  value: string;
  /** Stable analytics token when it differs from the option label (channel slug). */
  analyticsValue?: string;
};

export type CampaignsFilterApplyProperties = {
  filter_fields: CampaignsFilterField[];
  filter_values: Partial<
    Record<
      (typeof CAMPAIGNS_ENUM_FILTER_FIELDS)[CampaignsEnumFilterKey],
      string[]
    >
  >;
  filter_count: number;
  result_count: number;
};

export function isDateRangeApplied(dateRange: DateRangeFilter): boolean {
  return Boolean(dateRange.preset || dateRange.startDate || dateRange.endDate);
}

function resolveEnumValue(
  id: string,
  options: CampaignsFilterOption[] | undefined,
): string {
  const option = options?.find(item => item.value === id);
  if (option?.analyticsValue) return option.analyticsValue;
  if (option?.label) return option.label;
  return id;
}

/**
 * PostHog properties for `source_campaigns:filter_apply`. Built from the
 * filter state *after* the change. `filter_values` lists enum-typed facets
 * only (channel, status, position, location, brand) — date range is a
 * field, not an enum, so it is omitted from that object. `result_count` is
 * the campaigns total once the filtered list has loaded.
 */
export function buildCampaignsFilterApplyProperties({
  filters,
  dateRange,
  resultCount,
  optionsByKey,
}: {
  filters: Pick<Filters, CampaignsEnumFilterKey>;
  dateRange: DateRangeFilter;
  resultCount: number;
  optionsByKey: Partial<
    Record<CampaignsEnumFilterKey, CampaignsFilterOption[]>
  >;
}): CampaignsFilterApplyProperties {
  const filter_fields: CampaignsFilterField[] = [];
  const filter_values: CampaignsFilterApplyProperties['filter_values'] = {};

  (
    Object.keys(CAMPAIGNS_ENUM_FILTER_FIELDS) as CampaignsEnumFilterKey[]
  ).forEach(key => {
    const ids = filters[key];
    if (!ids.length) return;

    const field = CAMPAIGNS_ENUM_FILTER_FIELDS[key];
    filter_fields.push(field);
    filter_values[field] = ids.map(id =>
      resolveEnumValue(id, optionsByKey[key]),
    );
  });

  if (isDateRangeApplied(dateRange)) {
    filter_fields.push('date_range');
  }

  return {
    filter_fields,
    filter_values,
    filter_count: filter_fields.length,
    result_count: resultCount,
  };
}
